import { Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { CustomerHome } from './customer/CustomerHome'
import { CustomerJoin } from './customer/CustomerJoin'
import { Chatroom } from './chat/Chatroom'
import { AgentLogin } from './agent/AgentLogin'
import { AgentConsole } from './agent/AgentConsole'
import { ChatErrorBoundary } from './ChatErrorBoundary'
import { LAST_TENANT_SLUG_KEY } from './agent/last-slug'

export function App() {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-semibold text-slate-900">
            <span className="inline-block w-7 h-7 rounded bg-blue-600" aria-hidden />
            <span>SecureBridge<sup className="text-[10px] text-slate-400 ml-0.5">™</sup></span>
          </Link>
          <nav className="text-sm text-slate-600 space-x-5">
            <Link to="/" className="hover:text-slate-900">Customer</Link>
            <Link to="/agent" className="hover:text-slate-900">Agent</Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-10">
        <Routes>
          <Route path="/" element={<CustomerHome />} />
          <Route path="/join" element={<CustomerJoin />} />
          <Route
            path="/room/:id"
            element={
              <ChatErrorBoundary>
                <Chatroom role="customer" />
              </ChatErrorBoundary>
            }
          />
          {/*
            v0.2 agent routes are slug-scoped: the tenant is in the URL, not
            a typed-out ULID. Bare /agent and /agent/console redirect to the
            last slug used on this device, falling back to a small chooser.
          */}
          <Route path="/t/:slug/agent" element={<AgentLogin />} />
          <Route path="/t/:slug/agent/console" element={<AgentConsole />} />
          <Route path="/agent" element={<AgentEntryRedirect to="login" />} />
          <Route path="/agent/console" element={<AgentEntryRedirect to="console" />} />
          <Route
            path="/agent/room/:id"
            element={
              <ChatErrorBoundary>
                <Chatroom role="agent" />
              </ChatErrorBoundary>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <footer className="bg-white border-t border-slate-200 mt-10">
        <div className="max-w-5xl mx-auto px-6 py-4 text-xs text-slate-500 flex items-center justify-between">
          <span>© SecureBridge — secure chat companion for U.S. call centers</span>
          <span className="space-x-4">
            <a className="hover:text-slate-700" href="/privacy">Privacy</a>
            <a className="hover:text-slate-700" href="/terms">Terms</a>
          </span>
        </div>
      </footer>
    </div>
  )
}

/**
 * Redirect /agent and /agent/console to /t/<lastSlug>/agent[/console]. If
 * no last slug is remembered, render a small input asking for one — the
 * user can also bookmark a slug-prefixed URL directly. We avoid showing
 * a 404; this is a friendly entry point.
 */
function AgentEntryRedirect({ to }: { to: 'login' | 'console' }) {
  const navigate = useNavigate()
  const [slug, setSlug] = useState('')

  useEffect(() => {
    const last = localStorage.getItem(LAST_TENANT_SLUG_KEY)
    if (last && /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/.test(last)) {
      navigate(`/t/${last}/agent${to === 'console' ? '/console' : ''}`, { replace: true })
    }
  }, [navigate, to])

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const cleaned = slug.trim().toLowerCase()
    if (!cleaned) return
    navigate(`/t/${cleaned}/agent${to === 'console' ? '/console' : ''}`)
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
      <h1 className="text-xl font-semibold mb-2">Agent sign in</h1>
      <p className="text-sm text-slate-600 mb-6">
        Enter your company handle to continue. If you don't know it, ask
        your administrator.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Company handle</span>
          <div className="flex items-center rounded-lg border border-slate-300 focus-within:ring-2 focus-within:ring-blue-500">
            <span className="px-3 py-2 text-sm text-slate-500 select-none">securebridge.app/t/</span>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              autoFocus
              required
              spellCheck={false}
              autoComplete="off"
              placeholder="acme"
              className="flex-1 py-2 pr-3 text-sm outline-none bg-transparent"
            />
          </div>
        </label>
        <button
          type="submit"
          className="w-full rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700"
        >
          Continue
        </button>
      </form>
    </div>
  )
}
